import { useEffect, useRef, useState } from "react";

const REQUIRED_PASSWORD = "wedding";

export default function IntroTypewriter({ text = "THE BAKERS", onComplete }) {
  const [typed, setTyped] = useState("");
  const [typingDone, setTypingDone] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(true);
  const [passwordFullyVisible, setPasswordFullyVisible] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const inputRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Typewriter effect
  useEffect(() => {
    let i = 0;
    let intervalId = null;
    let endTimeoutId = null;
    const startTimeoutId = setTimeout(() => {
      intervalId = setInterval(() => {
        i += 1;
        setTyped(text.slice(0, i));
        if (i >= text.length) {
          clearInterval(intervalId);
          // brief pause then reveal password box
          endTimeoutId = setTimeout(() => {
            setTypingDone(true);
          }, 900);
        }
      }, 230);
    }, 3000);

    return () => {
      clearTimeout(startTimeoutId);
      if (intervalId) clearInterval(intervalId);
      if (endTimeoutId) clearTimeout(endTimeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  // Hide the blinking cursor once the password box has finished fading in.
  // Fade-in duration = 3000ms (matches the form's inline transition below).
  useEffect(() => {
    if (!typingDone) return;
    const t = setTimeout(() => setPasswordFullyVisible(true), 3000);
    return () => clearTimeout(t);
  }, [typingDone]);

  // When typing completes, let the cursor do one final natural blink (1s)
  // ending on the "off" beat, then unmount it — avoids a jarring mid-blink cut.
  useEffect(() => {
    if (!typingDone) return;
    const t = setTimeout(() => setCursorVisible(false), 1000);
    return () => clearTimeout(t);
  }, [typingDone]);

  // Autofocus password input when revealed
  useEffect(() => {
    if (typingDone && inputRef.current) {
      // small delay to let the fade-in start
      const t = setTimeout(() => inputRef.current?.focus(), 250);
      return () => clearTimeout(t);
    }
  }, [typingDone]);

  // Skip the typing animation if user clicks before it finishes (but NOT before password gate)
  const skipTyping = (e) => {
    if (e) e.stopPropagation();
    if (!typingDone) {
      setTyped(text);
      setTypingDone(true);
    }
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    const value = (inputRef.current?.value ?? password).trim().toLowerCase();
    if (value === REQUIRED_PASSWORD) {
      setError(false);
      setUnlocked(true);
      setTimeout(() => {
        onCompleteRef.current && onCompleteRef.current();
      }, 1200);
    } else {
      setError(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit(e);
    }
  };

  const handleChange = (e) => {
    setPassword(e.target.value);
    if (error) setError(false);
  };

  return (
    <div
      data-testid="intro-typewriter"
      onClick={(e) => {
        // Never let clicks inside the intro leak to the underlying <main>
        // (whose onClick advances the photo stack).
        e.stopPropagation();
        if (!typingDone) skipTyping(e);
      }}
      className={`fixed inset-0 z-[100] bg-black flex items-center justify-center transition-opacity duration-[1200ms] ease-out ${
        unlocked ? "opacity-0 pointer-events-none" : "opacity-100"
      } ${typingDone ? "cursor-default" : "cursor-pointer"}`}
    >
      <div
        className="text-white flex flex-col items-center"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontWeight: 500,
          letterSpacing: "0.01em",
          lineHeight: 1,
        }}
      >
        {/* Typewriter line */}
        <div className="flex items-center">
          <span data-testid="intro-text" className="text-base sm:text-lg">
            {typed}
          </span>
          {cursorVisible && (
            <span
              className={`ml-[3px] inline-block w-[1.5px] bg-white ${
                typingDone ? "cursor-blink-stop" : "cursor-blink"
              }`}
              style={{ height: "1em" }}
            />
          )}
        </div>

        {/* Password gate — fades in slowly after typing completes */}
        <form
          onSubmit={handleSubmit}
          className={`mt-10 flex flex-col items-center ${
            typingDone ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          style={{
            transition: "opacity 3000ms ease-in",
            willChange: "opacity",
          }}
          data-testid="password-gate-form"
        >
          <input
            ref={inputRef}
            data-testid="password-input"
            type="password"
            value={password}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="password"
            aria-label="password"
            className="bg-transparent border-0 border-b border-white/60 text-white text-sm sm:text-base text-center placeholder:text-white/40 focus:outline-none focus:border-white px-2 py-1 w-[220px] tracking-[0.18em] lowercase"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
            }}
          />
          <div
            data-testid="password-error"
            className={`mt-3 text-[11px] sm:text-xs uppercase tracking-[0.24em] transition-opacity duration-300 ${
              error ? "opacity-100" : "opacity-0"
            }`}
            style={{
              color: "#F4EBDD",
              fontFamily: "'Cormorant Garamond', serif",
              minHeight: "1em",
            }}
            aria-live="polite"
          >
            invalid password
          </div>
          {/* Subtle hint button — also triggers submit via direct onClick */}
          <button
            type="button"
            data-testid="password-submit"
            onClick={handleSubmit}
            className="mt-4 text-[11px] sm:text-xs uppercase tracking-[0.32em] text-white/60 [@media(hover:hover)]:hover:text-white active:text-white transition-colors cursor-pointer"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              letterSpacing: "0.32em",
            }}
          >
            enter
          </button>
        </form>
      </div>
    </div>
  );
}
